import isUndefined from 'lodash/isUndefined';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import pickBy from 'lodash/pickBy';

import {
  BehaviorMeasurement,
  InsightsResourceType,
  MATH_TYPE,
  MetricType,
  PRIMITIVE_MATH_TYPES,
  RETENTION_MATH_TYPES,
  TopLevelMetricType,
  XAU_MATH_TYPES,
} from 'common/types/reports/bookmark';

import {
  BehaviorMetric,
  BehaviorShowClause as BehaviorShowClauseType,
  FormulaMeasurement,
  FormulaReferencedMetrics,
  FormulaShowClause as FormulaShowClauseType,
  ShowClause as ShowClauseType,
  WarehouseShowClause as WarehouseShowClauseType,
} from '../util/types';
import {DATASET_MIXPANEL} from './constants';
import {FilterClause} from './filter-clause';
import {ClauseType} from './types';
import {isFormulaShowClause, isWarehouseShowClause, sanitizeIfCustomProperty} from './utils';

/** @deprecated use POJO, not this OOP model */
export abstract class ShowClause {
  static TYPE: typeof ClauseType.Show = ClauseType.Show;
  declare public isHidden: boolean;
  declare public isExpanded: boolean;
  declare public hasUnsavedChanges: boolean;
  declare public type: TopLevelMetricType.Metric | TopLevelMetricType.Formula | TopLevelMetricType.Warehouse;

  declare public _idx: string | undefined | null;

  static createShowClause(attrs: ShowClauseType): ShowClauseClassType {
    if (isFormulaShowClause(attrs)) {
      return new FormulaShowClause(attrs);
    }
    if (isWarehouseShowClause(attrs)) {
      return new WarehouseShowClause(attrs);
    }
    return new BehaviorShowClause(attrs);
  }

  abstract get attrs(): ShowClauseType;

  abstract get valid(): boolean;

  serialize(): ShowClauseType {
    return this.attrs;
  }

  abstract isRollingClause(): boolean;

  abstract isCumulativeClause(): boolean;

  abstract toBookmarkData(): ShowClauseType;

  abstract toUrlData(): ShowClauseType;
}

/** @deprecated use POJO, not this OOP model */
export type ShowClauseClassType = BehaviorShowClause | FormulaShowClause | WarehouseShowClause;

export class BehaviorShowClause extends ShowClause {
  declare id?: BehaviorMetric[`id`];
  declare name?: BehaviorMetric[`name`];
  declare userNamed?: BehaviorMetric[`userNamed`];
  declare behavior: BehaviorMetric[`behavior`];
  declare display?: BehaviorMetric[`display`];
  declare measurement: BehaviorMeasurement;
  declare goals?: BehaviorShowClauseType[`goals`];
  declare overrides?: BehaviorShowClauseType[`overrides`];
  type: TopLevelMetricType.Metric = TopLevelMetricType.Metric;

  constructor(attrs: BehaviorShowClauseType) {
    super();

    const {id, name, behavior, display, measurement, goals} = attrs;

    /** unique identifier for each clause */
    this._idx = attrs._idx;

    this.id = id ?? undefined;
    this.name = name;
    this.measurement = measurement ? {...measurement} : ({} as BehaviorMeasurement);
    this.display = display;
    this.goals = goals ?? null;
    this.isHidden = attrs.isHidden === true;
    this.isExpanded = attrs.isExpanded === true;
    this.hasUnsavedChanges = attrs.hasUnsavedChanges === true;
    this.behavior = behavior ?? null;
    this.overrides = attrs.overrides ?? undefined;

    if (!behavior || !measurement) {
      return;
    }

    if (attrs.userNamed) {
      this.userNamed = Boolean(attrs.userNamed);
    }

    this.behavior = {...behavior};
    this.behavior.dataset = behavior.dataset || null;
    this.behavior.dataset = this.behavior.dataset === `mixpanel` ? DATASET_MIXPANEL : this.behavior.dataset;
    this.behavior.resourceType = behavior.resourceType || InsightsResourceType.Events;
    this.behavior.profileType = behavior.profileType || null;
    this.behavior.search = behavior.search || ``;
    this.behavior.dataGroupId = behavior.dataGroupId || null;

    this.behavior.type = behavior.type;

    if (this.behavior.resourceType === InsightsResourceType.People) {
      this.behavior.profileType = InsightsResourceType.People;
    }

    let defaultMathType: MATH_TYPE = PRIMITIVE_MATH_TYPES.TOTAL;
    if (
      this.behavior &&
      this.behavior.type &&
      [MetricType.Retention, MetricType.RetentionFrequency].includes(this.behavior?.type)
    ) {
      defaultMathType = RETENTION_MATH_TYPES.RETENTION_RATE;
    }

    this.measurement.math = measurement.math || defaultMathType;
    this.measurement.perUserAggregation = measurement.perUserAggregation || null;
    this.measurement.rolling = measurement.rolling || null;
    this.measurement.cumulative = measurement.cumulative || false;

    // TODO: Move sanitization logic app api bookmark endpoint
    this.measurement.property = measurement.property
      ? omit(sanitizeIfCustomProperty(measurement.property), `label`)
      : null;
  }

  serializeFilter() {
    if (this.behavior?.filters?.length) {
      const filters = this.behavior.filters.map((clause, index) => ({
        ...new FilterClause(clause).serialize(),
        // Assign determiner to the first filter
        ...(index === 0 ? {determiner: this.behavior?.filtersDeterminer} : {}),
      }));

      return {filters};
    }
    return {};
  }

  serializeBehaviors() {
    if (!this.behavior?.behaviors) {
      return {};
    }

    return {
      behaviors: this.behavior.behaviors.map((b) => pickBy(b, (value) => !isUndefined(value))),
    };
  }

  getInlineCohort() {
    return this.behavior?.raw_cohort || null;
  }

  get isInlineCohort() {
    return !!this.getInlineCohort();
  }

  get attrs(): BehaviorShowClauseType {
    return {
      ...(this._idx ? {_idx: this._idx} : {}),
      ...(this.id ? {id: this.id} : {}),
      ...(this.name ? {name: this.name} : {}),
      ...(this.userNamed ? {userNamed: this.userNamed} : {}),
      ...(this.isHidden ? {isHidden: true} : {}),
      ...(this.isExpanded ? {isExpanded: true} : {}),
      ...(this.hasUnsavedChanges ? {hasUnsavedChanges: true} : {}),
      ...(this.goals ? {goals: this.goals} : {}),
      ...(this.overrides && Object.keys(this.overrides).length > 0 ? {overrides: this.overrides} : {}),
      behavior: {
        ...omitBy(
          {
            ...this.behavior,
            ...this.serializeFilter(),
            ...this.serializeBehaviors(),
          },
          isUndefined,
        ),
        resourceType: this.behavior?.resourceType,
      },
      display: omitBy(
        {
          ...this.display,
        },
        isUndefined,
      ) as BehaviorMetric[`display`],
      measurement: omitBy(
        {
          ...this.measurement,
        },
        isUndefined,
      ),
      type: TopLevelMetricType.Metric,
      ...(this.id ? {id: this.id} : {}),
    };
  }

  get customEventId() {
    if (this.behavior?.type === MetricType.CustomEvent) {
      return this.behavior.id;
    }
    return null;
  }

  get valid(): boolean {
    return true;
  }

  extend(attrs: Partial<BehaviorShowClauseType> = {}): BehaviorShowClause {
    const {behavior, measurement} = attrs;
    return new BehaviorShowClause({
      behavior: {
        ...this.behavior,
        ...omitBy(behavior, isUndefined),
        type: this.behavior?.type,
      },
      measurement: {
        ...this.measurement,
        ...omitBy(measurement, isUndefined),
      },
    });
  }

  toBookmarkData(): ShowClauseType {
    return pickBy(this.attrs, (value) => !isUndefined(value));
  }

  isXauClause(): boolean {
    return Object.values(XAU_MATH_TYPES).includes(this.measurement.math as XAU_MATH_TYPES);
  }

  isCumulativeUniqueClause(): boolean {
    return this.measurement.math === PRIMITIVE_MATH_TYPES.CUMULATIVE_UNIQUE;
  }

  isRollingClause(): boolean {
    return !!this.measurement.rolling;
  }

  isCumulativeClause(): boolean {
    return !!this.measurement.cumulative;
  }

  toUrlData(): BehaviorShowClauseType {
    return omit(this.attrs, `_idx`);
  }
}

/** @deprecated. use POJO, not OOP model */
export class FormulaShowClause extends ShowClause {
  declare measurement: FormulaMeasurement;
  declare display: any;
  declare id?: number;
  declare definition?: string;
  declare name?: string;
  declare referencedMetrics?: FormulaReferencedMetrics;
  declare goals?: FormulaShowClauseType[`goals`];
  declare overrides?: FormulaShowClauseType[`overrides`];
  type: TopLevelMetricType.Formula = TopLevelMetricType.Formula;

  constructor(attrs: FormulaShowClauseType) {
    super();
    this.display = attrs.display;
    this.measurement = attrs.measurement || {};
    this.goals = attrs.goals ?? null;
    this.hasUnsavedChanges = attrs.hasUnsavedChanges ?? false;
    this.overrides = attrs.overrides ?? undefined;

    /** unique identifier for each clause */
    this._idx = attrs._idx;

    if (attrs.id) {
      this.id = attrs.id;
    }

    this.definition = attrs.definition!;

    if (attrs.name) {
      this.name = attrs.name;
    }
    if (attrs.referencedMetrics) {
      this.referencedMetrics = attrs.referencedMetrics;
    }

    if (attrs.isHidden === true) {
      this.isHidden = true;
    }

    if (attrs.isExpanded === true) {
      this.isExpanded = true;
    }
  }

  get attrs(): FormulaShowClauseType {
    return this.serializeFormula();
  }

  private serializeFormula(): FormulaShowClauseType {
    return {
      ...(this._idx ? {_idx: this._idx} : {}),
      ...(this.isHidden ? {isHidden: true} : {}),
      ...(this.isExpanded ? {isExpanded: true} : {}),
      ...(this.display ? {display: this.display} : {}),
      ...(this.hasUnsavedChanges ? {hasUnsavedChanges: true} : {}),
      ...(this.goals ? {goals: this.goals} : {}),
      ...(this.overrides && Object.keys(this.overrides).length > 0 ? {overrides: this.overrides} : {}),
      definition: this.definition,
      id: this.id,
      measurement: {...this.measurement},
      name: this.name,
      referencedMetrics: this.referencedMetrics?.map((r) => new BehaviorShowClause(r).attrs),
      type: TopLevelMetricType.Formula,
    };
  }

  get valid(): boolean {
    return true;
  }

  isRollingClause(): boolean {
    return !!this.measurement.rolling;
  }

  isCumulativeClause(): boolean {
    return !!this.measurement.cumulative;
  }

  extend(attrs: Partial<FormulaShowClauseType> = {}): FormulaShowClause {
    return new FormulaShowClause({
      definition: this.definition,
      display: {
        ...this.display,
      },
      id: this.id,
      measurement: {
        ...this.measurement,
        ...attrs.measurement,
      },
      name: this.name,
      referencedMetrics: this.referencedMetrics,
      type: TopLevelMetricType.Formula,
    });
  }

  toBookmarkData(): FormulaShowClauseType {
    return this.serializeFormula();
  }

  toUrlData(): FormulaShowClauseType {
    return this.serializeFormula();
  }
}

/** @deprecated. use POJO, not OOP model */
export class WarehouseShowClause extends ShowClause {
  declare query: WarehouseShowClauseType[`query`];
  declare metricType: WarehouseShowClauseType[`metricType`];
  declare warehouseSourceId: WarehouseShowClauseType[`warehouseSourceId`];
  declare timeColumn?: WarehouseShowClauseType[`timeColumn`];
  declare valueColumn?: WarehouseShowClauseType[`valueColumn`];
  declare syncInterval?: WarehouseShowClauseType[`syncInterval`];
  declare aggregation?: WarehouseShowClauseType[`aggregation`];
  declare measurement?: WarehouseShowClauseType[`measurement`];
  declare display?: WarehouseShowClauseType[`display`];
  declare id?: number;
  declare name?: string;
  declare userNamed?: boolean;
  declare labelPrefix?: WarehouseShowClauseType[`labelPrefix`];
  declare goals?: WarehouseShowClauseType[`goals`];
  declare overrides?: WarehouseShowClauseType[`overrides`];
  type: TopLevelMetricType.Warehouse = TopLevelMetricType.Warehouse;

  constructor(attrs: WarehouseShowClauseType) {
    super();

    /** unique identifier for each clause */
    this._idx = attrs._idx;

    this.query = attrs.query;
    this.metricType = attrs.metricType;
    this.aggregation = attrs.aggregation;
    this.warehouseSourceId = attrs.warehouseSourceId;
    this.timeColumn = attrs.timeColumn ?? null;
    this.valueColumn = attrs.valueColumn ?? null;
    this.syncInterval = attrs.syncInterval ?? `daily`;
    this.measurement = attrs.measurement ?? null;
    this.display = attrs.display ?? null;
    this.labelPrefix = attrs.labelPrefix ?? null;
    this.goals = attrs.goals ?? null;
    this.overrides = attrs.overrides ?? undefined;
    this.isHidden = attrs.isHidden === true;
    this.isExpanded = attrs.isExpanded === true;
    this.hasUnsavedChanges = attrs.hasUnsavedChanges === true;

    if (attrs.id) {
      this.id = attrs.id;
    }
    if (attrs.name) {
      this.name = attrs.name;
    }
    if (attrs.userNamed) {
      this.userNamed = Boolean(attrs.userNamed);
    }
  }

  get attrs(): WarehouseShowClauseType {
    return this.serializeAttrs();
  }

  private serializeAttrs(): WarehouseShowClauseType {
    return omitBy(
      {
        ...(this._idx ? {_idx: this._idx} : {}),
        ...(this.id ? {id: this.id} : {}),
        ...(this.name ? {name: this.name} : {}),
        ...(this.userNamed ? {userNamed: this.userNamed} : {}),
        ...(this.isHidden ? {isHidden: true} : {}),
        ...(this.isExpanded ? {isExpanded: true} : {}),
        ...(this.hasUnsavedChanges ? {hasUnsavedChanges: true} : {}),
        ...(this.goals ? {goals: this.goals} : {}),
        ...(this.overrides && Object.keys(this.overrides).length > 0 ? {overrides: this.overrides} : {}),
        query: this.query,
        metricType: this.metricType,
        warehouseSourceId: this.warehouseSourceId,
        timeColumn: this.timeColumn,
        syncInterval: this.syncInterval,
        valueColumn: this.valueColumn,
        aggregation: this.aggregation,
        measurement: this.measurement,
        display: this.display,
        labelPrefix: this.labelPrefix,
        type: TopLevelMetricType.Warehouse,
      } satisfies WarehouseShowClauseType,
      isUndefined,
    ) as unknown as WarehouseShowClauseType;
  }

  get valid(): boolean {
    return !!this.query && !!this.warehouseSourceId;
  }

  isRollingClause(): boolean {
    return false;
  }

  isCumulativeClause(): boolean {
    return false;
  }

  extend(attrs: Partial<WarehouseShowClauseType> = {}): WarehouseShowClause {
    return new WarehouseShowClause({
      ...this.serializeAttrs(),
      ...omitBy(attrs, isUndefined),
      type: TopLevelMetricType.Warehouse,
    });
  }

  toBookmarkData(): WarehouseShowClauseType {
    return this.serializeAttrs();
  }

  toUrlData(): WarehouseShowClauseType {
    return omit(this.serializeAttrs(), `_idx`);
  }
}
